import PropTypes from 'prop-types';

import { Box, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import CommentInputContainer from '../Comment/CommentInputContainer';
import CommunityCommentContainer from '../Comment/CommunityCommentContainer';

import CarouselComponent from '../../Image/CarouselComponent';
import ExpandableImageComponent from '../../Image/ExpandableImageComponent';
import PriceComponent from '../components/PriceComponent';

import { avatarPlaceholderSRC, isAdmin, loadUser } from '../../../utils';
import { getFullName } from '../../Person/utils';

import { COMMUNITY_POST_TYPES } from '../constants';

import { ButtonLink } from '../../Button';
import WithLink from '../../Linkify';
import { PostHeader } from '../../Shared/post';
import CommentsButton from '../components/CommentsButton';
import LikesButton from '../components/LikesButton';
import MessageSellerButton from '../components/MessageSellerButton';
import StatusBadge from '../components/StatusBadge';

const StyledContainer = styled(Paper)(({ theme }) => ({
  margin: '2rem 0 0 0',
  borderRadius: '1rem',
  padding: '2rem 4rem',
  [theme.breakpoints.down('md')]: {
    padding: '2rem',
  },
}));

const StyledText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFullTextShown',
})(({ isFullTextShown }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  whiteSpace: 'break-spaces',
  ...(!isFullTextShown && {
    display: '-webkit-box',
    WebKitBoxOrient: 'vertical',
    WebkitLineClamp: '3',
  }),
}));

const CommunityPostItemComponent = (props) => {
  const {
    isShowComments,
    onToggleComments,
    post,
    dropDownActions,
    onTogglePostLike,
    onMessageSeller,
    isFullTextShown,
    setIsFullTextShown,
    isOverflown,
    textElementRef,
    canCommentPost,
    canMessageSeller,
    isInReview,
  } = props;

  const currentUser = loadUser();

  const {
    community,
    createdAt,
    id,
    photos,
    price,
    sender,
    text,
    type,
    url,
    commentsCount,
    likesCount,
    likedByViewer,
    subject,
  } = post;

  const images =
    photos?.map(({ url, id }) => ({
      id,
      source: { regular: url, fullscreen: url, thumbnail: url },
    })) ?? [];

  return (
    <StyledContainer elevation={0}>
      <PostHeader
        creator={sender}
        creatorInfo={community?.name ?? 'Community'}
        dropDownActions={dropDownActions}
        createdAt={createdAt}
      />

      <Box mt="1rem" />

      {type === COMMUNITY_POST_TYPES.marketplace && (
        <>
          <PriceComponent price={price} />
          <span>{subject}</span>
        </>
      )}

      {type !== COMMUNITY_POST_TYPES.marketplace && (
        <Typography fontWeight="bold">{subject}</Typography>
      )}

      <Typography
        ref={textElementRef}
        component="div"
        maxHeight={isFullTextShown ? 'auto' : '7rem'}
        overflow={isFullTextShown ? 'auto' : 'hidden'}
      >
        <StyledText>
          <WithLink>{text}</WithLink>
        </StyledText>
      </Typography>

      {isOverflown ? (
        <ButtonLink onClick={() => setIsFullTextShown(!isFullTextShown)}>
          See {isFullTextShown ? 'less' : 'more'}
        </ButtonLink>
      ) : null}

      <div>
        <WithLink>{url}</WithLink>
      </div>

      <Box m="1rem 1rem 1rem 0">
        <CarouselComponent images={images} height="10rem" isSquare />
      </Box>

      {isInReview && <StatusBadge>Post in review</StatusBadge>}

      {type !== COMMUNITY_POST_TYPES.marketplace && (
        <Box display="flex" gap="2rem" ml="-1rem" mb="1rem">
          <CommentsButton
            onClick={onToggleComments}
            commentsCount={commentsCount}
            disabled={!commentsCount}
          />
          <LikesButton
            onClick={onTogglePostLike}
            likesCount={likesCount}
            isLiked={likedByViewer}
            disabled={isAdmin() || isInReview}
          />
        </Box>
      )}

      {type !== COMMUNITY_POST_TYPES.marketplace && isShowComments && (
        <CommunityCommentContainer currentPostId={id} />
      )}

      {type === COMMUNITY_POST_TYPES.marketplace && canMessageSeller && (
        <Box ml="-1rem">
          <MessageSellerButton onClick={onMessageSeller} />
        </Box>
      )}

      {type !== COMMUNITY_POST_TYPES.marketplace && canCommentPost && (
        <Box display="flex" gap="1rem">
          <Box alignSelf="center">
            <ExpandableImageComponent
              photo={currentUser?.photo}
              src={avatarPlaceholderSRC}
              caption={getFullName(currentUser)}
              height="3.2rem"
              isSquare
            />
          </Box>
          <CommentInputContainer postId={id} disabled={isInReview} />
        </Box>
      )}
    </StyledContainer>
  );
};

CommunityPostItemComponent.propTypes = {
  post: PropTypes.instanceOf(Object).isRequired,
  isShowComments: PropTypes.bool.isRequired,
  dropDownActions: PropTypes.instanceOf(Array).isRequired,
  onTogglePostLike: PropTypes.func.isRequired,
  onMessageSeller: PropTypes.func.isRequired,
  isFullTextShown: PropTypes.bool.isRequired,
  setIsFullTextShown: PropTypes.func.isRequired,
  isOverflown: PropTypes.bool.isRequired,
  textElementRef: PropTypes.instanceOf(Object).isRequired,
  canCommentPost: PropTypes.bool.isRequired,
  canMessageSeller: PropTypes.bool.isRequired,
  onToggleComments: PropTypes.func.isRequired,
  isInReview: PropTypes.bool.isRequired,
};

export default CommunityPostItemComponent;
